import { createAppKit, type CreateAppKit } from '@reown/appkit/vue'

import {
  type AppKitNetwork,
  base,
  baseSepolia,
} from '@reown/appkit/networks'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { env } from '@/env.ts'
import { createClient, encodeFunctionData, http } from 'viem'
import { normalizeEip155ChainId } from '@/composables/wallet/chain-id.ts'

export const lingoMainnet = env.app.isProd ? base : baseSepolia

export const metadata = {
  name: 'Lingo dev',
  description: 'Lingo token',
  url: window.location.origin, // origin must match your domain & subdomain
  icons: ['https://avatars.githubusercontent.com/u/37784886'],
}

const networks: [AppKitNetwork, ...AppKitNetwork[]] = [lingoMainnet]

export const wagmiAdapter = new WagmiAdapter({
  projectId: env.reown.projectId,
  networks,
  client({ chain }) {
    return createClient({ chain, transport: http(env.contracts.rpcEndpoint) })
  },
})
export const config = wagmiAdapter.wagmiConfig

const writeContract = wagmiAdapter.writeContract.bind(wagmiAdapter)

wagmiAdapter.writeContract = async (params) => {
  const chainId = normalizeEip155ChainId(params.caipNetwork.id ?? params.caipNetwork.caipNetworkId)
  const provider = params.provider as { request?: (args: { method: string, params?: unknown[] }) => Promise<unknown> } | undefined

  if (!chainId || !provider?.request) {
    return writeContract(params)
  }

  const hash = await provider.request({
    method: 'eth_sendTransaction',
    params: [{
      from: params.fromAddress,
      to: params.tokenAddress,
      chainId: `0x${chainId.toString(16)}`,
      data: encodeFunctionData({
        abi: params.abi,
        functionName: params.method,
        args: params.args,
      }),
      value: '0x0',
    }],
  })

  return { hash: hash as string }
}

/**
 * URL params:
 * To enable web2 only mode, set ?web2only=true
 * To enable email auth, set ?auth=email
 * To enable web2 auth, set ?auth=web2
 */
const authParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const auth = urlParams.get('auth') as 'email' | 'web2'
  const web2Only = urlParams.get('web2only') === 'true'
  return {
    isWeb2Only: web2Only || Boolean(auth),
    authEmail: auth === 'email',
    auth,
  }
}

export const { isWeb2Only, authEmail, auth } = authParams()

const allWallets: CreateAppKit['allWallets'] = isWeb2Only ? 'HIDE' : 'SHOW'

export const appkit = createAppKit({
  enableAuthLogger: true,
  enableNetworkSwitch: false,
  adapters: [wagmiAdapter],
  networks,
  defaultNetwork: lingoMainnet,
  defaultAccountTypes: { eip155: 'eoa' },
  metadata,
  allWallets,
  enableWallets: !isWeb2Only,
  projectId: env.reown.projectId,
  features: authEmail
    ? {
      analytics: true, // Optional - defaults to your Cloud configuration
      socials: [],
    }
    : {
      analytics: true, // Optional - defaults to your Cloud configuration
    },
})
